import type React from 'react';
import { useState, useEffect, useCallback, useRef } from 'react';

// mui
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import type { ChipTypeMap } from '@mui/material';
import LinearProgress from '@mui/material/LinearProgress';
import type { AutocompleteProps } from '@mui/material';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { type SxProps, type Theme } from '@mui/material/styles';

// react-infinite-scroll-hook
import useInfiniteScroll from 'react-infinite-scroll-hook';

// use-debounce hook
import { useDebounce } from 'use-debounce';

// autosuggest-highlight
import match from 'autosuggest-highlight/match';
import parse from 'autosuggest-highlight/parse';

import handleError from 'utils-ts/handle-error';
import callAzureFunctionPublic from 'utils-ts/call-azure-function-public';
import callAzureFunction from 'utils-ts/call-azure-function';

// ==============================|| CUSTOM AUTOCOMPLETE WITH INFINITE SCROLL ||============================== //

interface OptionsResponse<T> {
  rows: T[];
  hasNextPage: boolean;
  nextLink?: string | null;
}

/**
 * Shape every option must satisfy so the component can key and identify items.
 * Extend this in consuming code as needed (e.g. `interface MyOption extends BaseOption { name: string }`).
 */
export interface BaseOption {
  id: string;
  deletedAt?: string | null;
}

interface CustomAutocompleteProps<
  Value extends BaseOption,
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined = false,
  FreeSolo extends boolean | undefined = false,
  ChipComponent extends React.ElementType = ChipTypeMap['defaultComponent']
> extends Partial<AutocompleteProps<Value, Multiple, DisableClearable, FreeSolo, ChipComponent>> {
  appDataSourceId?: string;
  optionsUrl: string;
  optionsUrlIsPublic?: boolean;
  optionsUrlParams?: Record<string, unknown>;
  optionsPageSize?: number;
  optionsFirstPageNumber?: number;
  optionsHighlight?: boolean;
  optionsHighlightColor?: string;
  minSearchInput?: number;
  sx?: SxProps<Theme>;
  renderInputLabel?: string;
  overwriteInputValue?: string | null;
  setOverwriteInputValue?: (event: React.SyntheticEvent | null, value: string | null | undefined) => void;
  fullWidth?: boolean;
  /**
   * When provided, the component will fetch the matching option on mount and
   * use it as the controlled `value`.
   *
   * Pass the raw id string (or `null`/`undefined` to clear).
   */
  initialValueId?: string | null;
  /**
   * Same as `initialValueId`, but for multi-select (e.g. a tagbox). Pass the
   * list of already-selected ids so their labels can be fetched and shown.
   */
  initialValueIds?: string[] | null;
  /**
   * Called once with the full items for `initialValueIds`, as soon as they're
   * fetched. Use this if you need those items before the user picks anything.
   */
  onInitialValuesResolved?: (values: Value[]) => void;
  /** Show error state on the input */
  error?: boolean;
  /** Helper text to display below the input */
  helperText?: React.ReactNode;
  /** Called when the input loses focus */
  onBlur?: (event: React.FocusEvent) => void;
}

const AutocompleteWithInfiniteScroll = <
  Value extends BaseOption,
  Multiple extends boolean | undefined = false,
  DisableClearable extends boolean | undefined = false,
  FreeSolo extends boolean | undefined = false,
  ChipComponent extends React.ElementType = ChipTypeMap['defaultComponent']
>(
  props: CustomAutocompleteProps<Value, Multiple, DisableClearable, FreeSolo, ChipComponent>
) => {
  const { error, helperText, onBlur, ...restProps } = props;
  const {
    appDataSourceId,
    optionsUrl,
    optionsUrlIsPublic = false,
    optionsUrlParams = {},
    optionsPageSize = 25,
    optionsFirstPageNumber = 0,
    optionsHighlight = false,
    optionsHighlightColor = '',
    minSearchInput = 0,
    sx = {},
    renderInput,
    renderInputLabel,
    renderOption: customRenderOption,
    overwriteInputValue = undefined,
    setOverwriteInputValue = undefined,
    initialValueId = undefined,
    initialValueIds = undefined,
    onInitialValuesResolved = undefined,
    ...others
  } = props;

  const { limitTags } = others;

  const [open, setOpen] = useState<boolean>(false);
  // options
  const [options, setOptions] = useState<Value[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [hasNextPage, setHasNextPage] = useState<boolean>(false);
  const [page, setPage] = useState<number>(optionsFirstPageNumber);
  const [nextLink, setNextLink] = useState<string | null>(null);
  // input value
  const [inputValue, setInputValue] = useState<string>('');
  // search (only set when length of input value >= minSearchInput)
  const [search, setSearch] = useState<string>('');
  // debounced search value
  const [searchValue] = useDebounce<string>(search, 800);

  // ── Initial value resolution ────────────────────────────────────────────────
  // When `initialValueId` is supplied we fetch the matching option once so the
  // Autocomplete can display the full option object (label, etc.) rather than
  // just the raw id string.
  const [resolvedValue, setResolvedValue] = useState<Value | null>(null);
  const [initialValueLoading, setInitialValueLoading] = useState<boolean>(false);

  useEffect(() => {
    let isMounted = true;

    if (initialValueId) {
      // If the resolved value is already correct, don't re-fetch
      if (resolvedValue?.id === initialValueId) {
        return;
      }

      // If the selected option is already in the list, use it directly
      const existingOption = options.find((o) => o.id === initialValueId);
      if (existingOption) {
        setResolvedValue(existingOption);
        return;
      }

      setInitialValueLoading(true);

      callAzureFunction({
        url: optionsUrl,
        method: 'get',
        params: { ...optionsUrlParams, id: initialValueId }
      })
        .then((response) => {
          const option = (response?.data as OptionsResponse<Value>)?.rows?.find((r) => r.id === initialValueId);
          if (option && isMounted) {
            setResolvedValue(option);
          }
        })
        .catch(handleError)
        .finally(() => {
          if (isMounted) setInitialValueLoading(false);
        });
    } else {
      setResolvedValue(null);
    }

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValueId, optionsUrl]);

  // ── Initial values resolution (multi-select) ────────────────────────────────
  // Same idea as `initialValueId` above, but for a list of ids: turns
  // `initialValueIds` into full items so the tags have labels to show.
  //
  // `resolvedValues` doubles as "what's currently selected": once the fetch
  // finishes we show these items, and from then on every user edit updates
  // this same state (see the `onChange` wrapper below). That way the field
  // stays controlled the whole time instead of switching on and off, which
  // React/MUI would otherwise warn about.
  const [resolvedValues, setResolvedValues] = useState<Value[]>([]);
  const [initialValuesLoading, setInitialValuesLoading] = useState<boolean>(false);

  // Arrays are compared by reference in a dependency list, so a new array with
  // the same ids would still look "different" and cause an endless refetch.
  // Joining to a plain string avoids that.
  const initialValueIdsKey = (initialValueIds ?? []).join(',');

  useEffect(() => {
    let isMounted = true;

    if (initialValueIdsKey) {
      const ids = initialValueIdsKey.split(',');

      // If every id is already resolved, don't re-fetch
      const resolvedIds = new Set(resolvedValues.map((v) => v.id));
      if (ids.every((id) => resolvedIds.has(id))) {
        return;
      }

      setInitialValuesLoading(true);

      // Fetch all the requested ids in a single request, using the `ids`
      // filter added to items-autocomplete for this.
      callAzureFunction({
        url: optionsUrl,
        method: 'get',
        params: { ...optionsUrlParams, ids: initialValueIdsKey }
      })
        .then((response) => {
          const rows = (response?.data as OptionsResponse<Value>)?.rows ?? [];
          if (isMounted) {
            setResolvedValues(rows);
            onInitialValuesResolved?.(rows);
          }
        })
        .catch(handleError)
        .finally(() => {
          if (isMounted) setInitialValuesLoading(false);
        });
    } else {
      setResolvedValues([]);
    }

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValueIdsKey, optionsUrl]);

  // ── Option fetching ─────────────────────────────────────────────────────────

  const getOptions = useCallback(
    async (params: Record<string, unknown> = {}) => {
      let response;

      if (optionsUrlIsPublic) {
        response = await callAzureFunctionPublic({ url: optionsUrl, method: 'get', params, appDataSourceId });
      } else {
        response = await callAzureFunction({ url: optionsUrl, method: 'get', params, appDataSourceId });
      }

      return response.data as OptionsResponse<Value>;
    },
    [appDataSourceId, optionsUrl, optionsUrlIsPublic]
  );

  // Use useRef to maintain a stable reference for optionsUrlParams otherwise it loops
  const optionsUrlParamsRef = useRef(optionsUrlParams);
  optionsUrlParamsRef.current = optionsUrlParams;
  const optionsUrlParamsKey = JSON.stringify(optionsUrlParams ?? {});

  const getInitialOptions = useCallback(async () => {
    try {
      setLoading(true);
      setPage(optionsFirstPageNumber);
      setNextLink(null);

      const params = {
        ...optionsUrlParamsRef.current,
        // when getting an initial set of options, always start from first page
        page: optionsFirstPageNumber,
        pageSize: optionsPageSize,
        search: searchValue
      };

      const { rows, hasNextPage, nextLink: responseNextLink } = await getOptions(params);

      setOptions(rows);
      setHasNextPage(hasNextPage);
      setNextLink(responseNextLink ?? null);
    } catch (error) {
      handleError(error);
    } finally {
      setLoading(false);
    }
  }, [getOptions, optionsFirstPageNumber, optionsPageSize, searchValue]);

  useEffect(() => {
    // when the search value has enough characters, get the initial options
    if (open && searchValue.length >= minSearchInput) {
      getInitialOptions();
    }
  }, [getInitialOptions, minSearchInput, open, searchValue, optionsUrlParamsKey]);

  const loadMoreOptions = async () => {
    try {
      setLoading(true);

      const params = {
        ...optionsUrlParamsRef.current,
        page: page + 1,
        pageSize: optionsPageSize,
        search: searchValue,
        // forward the OData nextLink cursor when available so backends that
        // use token-based pagination (e.g. D365F) can use it directly
        ...(nextLink ? { nextLink } : {})
      };

      const { rows, hasNextPage, nextLink: responseNextLink } = await getOptions(params);

      // append new options
      setOptions((prev) => [...prev, ...rows]);
      setHasNextPage(hasNextPage);
      setNextLink(responseNextLink ?? null);
      // only advance the page counter after a successful fetch
      setPage((prev) => prev + 1);
    } catch (error) {
      handleError(error);
    } finally {
      setLoading(false);
    }
  };

  const [sentryRef, { rootRef: infiniteScrollRef }] = useInfiniteScroll({
    loading,
    hasNextPage,
    disabled: !open,
    onLoadMore: () => loadMoreOptions()
  });

  // ── Input handling ──────────────────────────────────────────────────────────

  const handleInputChange = (e: React.SyntheticEvent | null, newInputValue: string) => {
    if (e?.type === 'change' && newInputValue?.length >= minSearchInput) {
      // update search string
      setSearch(newInputValue);
    } else {
      // not enough characters to trigger search
      setSearch('');
      // Retain the resolved value so MUI can still display the selected option
      setOptions(resolvedValue ? [resolvedValue] : []);
    }

    // if (others.freeSolo && others.multiple && e) {
    //   if (newInputValue.endsWith(',')) {
    //     e.target.blur();
    //     e.target.focus();
    //     return;
    //   }

    //   if (newInputValue.endsWith(' ')) {
    //     e.target.blur();
    //     e.target.focus();
    //     return;
    //   }
    // }

    setPage(optionsFirstPageNumber);
    setNextLink(null);
    setHasNextPage(false);
    setInputValue(newInputValue);

    if (setOverwriteInputValue) {
      setOverwriteInputValue(e, newInputValue);
    }
  };

  // ── Render helpers ──────────────────────────────────────────────────────────

  const renderSentry = () =>
    hasNextPage || loading ? (
      <li ref={sentryRef} key="sentry">
        <LinearProgress />
      </li>
    ) : null;

  const handleNoOptionsText = () => {
    if (search.length < minSearchInput) {
      return `Input at least ${minSearchInput} characters to execute search.`;
    }
    return 'No options found. Enter a search text to try again.';
  };

  const resolvedNoOptionsText = others.noOptionsText ?? handleNoOptionsText();

  // Decide what value to show in the field:
  // - if the caller already passed their own `value` prop, leave it alone
  // - if we were given an id (or ids) to look up, show what we fetched
  // - otherwise let MUI manage the value itself (the normal case)
  const callerControlsValue = 'value' in others;

  // MUI's `value` prop only accepts a value or `undefined`, never `null`, so
  // `resolvedValue`'s "nothing yet" state (`null`) is converted to `undefined`.
  type ControlledValue = Parameters<typeof Autocomplete>[0]['value'];
  let controlledValue: ControlledValue;
  if (callerControlsValue) {
    controlledValue = undefined;
  } else if (initialValueId !== undefined) {
    controlledValue = (resolvedValue ?? undefined) as ControlledValue;
  } else if (initialValueIds !== undefined) {
    controlledValue = resolvedValues as ControlledValue;
  } else {
    controlledValue = undefined;
  }

  const combinedLoading = loading || initialValueLoading || initialValuesLoading || (others.loading ?? false);

  if ((initialValueId && initialValueLoading) || (initialValueIdsKey && initialValuesLoading)) {
    return <TextField disabled fullWidth={others.fullWidth ?? true} label={renderInputLabel} value="Loading..." />;
  }

  // TypeScript struggles to type-check the merged props below, even though
  // they're valid at runtime, so we cast the final object once here.
  const autocompleteProps = {
    ...restProps,
    ...(controlledValue !== undefined ? { value: controlledValue } : {}),
    // When using `initialValueIds`, also update `resolvedValues` on every
    // user edit, so the tag they just picked/removed shows up right away
    // (see the comment above `resolvedValues` for why).
    onChange: (event: React.SyntheticEvent, newValue: unknown, ...rest: unknown[]) => {
      if (initialValueIds !== undefined) {
        setResolvedValues(newValue as Value[]);
      }
      (restProps.onChange as ((...args: unknown[]) => void) | undefined)?.(event, newValue, ...rest);
    }
  } as React.ComponentProps<typeof Autocomplete<Value, Multiple, DisableClearable, FreeSolo, ChipComponent>>;

  return (
    <Autocomplete
      {...autocompleteProps}
      sx={{
        ...sx
        // add custom styles here
      }}
      // by default tags are limited to 5 unless a different limit is provided
      limitTags={typeof limitTags === 'number' ? limitTags : 5}
      slotProps={{
        listbox: {
          style: {
            maxHeight: 300,
            overflow: 'auto'
          },
          // apply ref from useInfiniteScroll hook to Listbox
          ref: infiniteScrollRef
        }
      }}
      loading={combinedLoading}
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      options={options}
      noOptionsText={resolvedNoOptionsText}
      filterOptions={(options, state) => {
        const { filterOptions: customFilterOptions } = props;
        let filteredOptions = options;
        if (customFilterOptions) {
          filteredOptions = customFilterOptions(options, state);
        }

        if (hasNextPage) {
          filteredOptions.push({ id: 'sentry', label: '' } as unknown as Value);
        }

        return filteredOptions;
      }}
      renderInput={
        renderInput ??
        ((params) => <TextField {...params} label={renderInputLabel ?? 'Options'} error={error} helperText={helperText} onBlur={onBlur} />)
      }
      // onBlur is handled by TextField, not Autocomplete
      renderOption={(renderOptionProps, option, _state, ownerState) => {
        const id = option.id as React.Key | null | undefined;
        const deletedAt = option.deletedAt ?? null;

        if (id === 'sentry') {
          return renderSentry();
        }

        if (customRenderOption) {
          return customRenderOption(renderOptionProps, option, _state, ownerState);
        }

        // use 'getOptionLabel' function, else default to 'label'
        const label = ownerState.getOptionLabel(option);

        let parts: { text: string; highlight: boolean }[] = [];
        if (optionsHighlight && id !== 'sentry') {
          const matches = match(label, search, { insideWords: true, findAllOccurrences: true });
          parts = parse(label, matches);
        }

        return (
          <li {...renderOptionProps} key={id}>
            {optionsHighlight ? (
              <>
                {/* render parts with highlight */}
                {parts.length > 0 ? (
                  <Box>
                    {parts.map((part, index) => (
                      <Box
                        key={index}
                        component="span"
                        sx={{
                          fontWeight: part.highlight ? 'bold' : 'regular',
                          color: part.highlight && optionsHighlightColor ? optionsHighlightColor : 'unset'
                        }}
                      >
                        <Typography component="span" color={deletedAt ? 'error' : 'inherit'}>
                          {part.text}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                ) : (
                  // render label if there are no parts
                  <>{label}</>
                )}
              </>
            ) : (
              // render label if highlighting is not enabled
              <>{label}</>
            )}
          </li>
        );
      }}
      // input value
      inputValue={overwriteInputValue ?? inputValue}
      onInputChange={handleInputChange}
    />
  );
};

export default AutocompleteWithInfiniteScroll;
